import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ImprovedListMessageForm } from '@/components/forms/ImprovedListMessageForm';
import { ImprovedButtonMessageForm } from '@/components/forms/ImprovedButtonMessageForm';
import { ListMessagePreview } from '@/components/preview/ListMessagePreview';
import { ButtonMessagePreview } from '@/components/preview/ButtonMessagePreview';
import { SampleListPreview, SampleButtonPreview } from '@/components/preview/SamplePreviews';
import { I18nProvider } from '@/components/I18nProvider';
import { LanguageSelector } from '@/components/LanguageSelector';
import { ListFormData, ButtonFormData, ListMessage, ButtonMessage } from '@/types/messages';
import { parseListFormToPreview, parseButtonFormToPreview, generateListMessage, generateButtonMessage } from '@/utils/messageGenerator';
import { parseMessageToForm } from '@/utils/messageParser';
import { Copy, Check, List, MessageSquare, Smartphone, AlertTriangle } from 'lucide-react';

function App() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<'list' | 'button'>('button');
  const [listFormData, setListFormData] = useState<ListFormData>({
    title: '',
    description: '',
    footer: '',
    buttonText: '',
    sections: []
  });
  const [buttonFormData, setButtonFormData] = useState<ButtonFormData>({
    title: '',
    subTitle: '',
    mediaType: 'none',
    mediaUrl: '',
    buttons: []
  });
  const [generatedMessage, setGeneratedMessage] = useState<string>('');
  const [copied, setCopied] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [importText, setImportText] = useState('');
  const [importError, setImportError] = useState<string | null>(null);

  // Auto-generate messages when form data changes
  useEffect(() => {
    if (activeTab === 'list' && listFormData.sections.length > 0 && 
        listFormData.sections.every(section => 
          section.name && 
          section.items && 
          section.items.length > 0 && 
          section.items.every(item => item.title && item.optionId)
        ) &&
        listFormData.description) {
      const message = generateListMessage(listFormData);
      setGeneratedMessage(message);
    } else if (activeTab === 'button' && buttonFormData.buttons.length > 0 &&
               buttonFormData.title && 
               buttonFormData.buttons.every(button => {
                 if (!button.text) return false;
                 if (button.type === 'quick_reply' && !button.id) return false;
                 if (button.type === 'cta_url' && !button.url) return false;
                 if (button.type === 'cta_call' && !button.phoneNumber) return false;
                 if (button.type === 'cta_copy' && !button.copyCode) return false;
                 return true;
               })) {
      const message = generateButtonMessage(buttonFormData);
      setGeneratedMessage(message);
    } else {
      setGeneratedMessage('');
    }
  }, [activeTab, listFormData, buttonFormData]);

  const handleListFormChange = useCallback((data: ListFormData) => {
    setListFormData(data);
  }, []);

  const handleButtonFormChange = useCallback((data: ButtonFormData) => {
    setButtonFormData(data);
  }, []);

  const handleListGenerate = useCallback((message: string) => {
    setGeneratedMessage(message);
  }, []);

  const handleButtonGenerate = useCallback((message: string) => {
    setGeneratedMessage(message);
  }, []);

  const handleCopyMessage = async () => {
    if (generatedMessage) {
      await navigator.clipboard.writeText(generatedMessage);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const getPreviewData = (): { list?: ListMessage; button?: ButtonMessage } => {
    if (activeTab === 'list' && listFormData.sections.length > 0 && 
        listFormData.sections.every(section => 
          section.name && 
          section.items && 
          section.items.length > 0 && 
          section.items.every(item => item.title && item.optionId)
        )) {
      return { list: parseListFormToPreview(listFormData) };
    }
    if (activeTab === 'button' && buttonFormData.buttons.length > 0 &&
        buttonFormData.buttons.every(button => {
          if (!button.text) return false;
          if (button.type === 'quick_reply' && !button.id) return false;
          if (button.type === 'cta_url' && !button.url) return false;
          if (button.type === 'cta_call' && !button.phoneNumber) return false;
          if (button.type === 'cta_copy' && !button.copyCode) return false;
          return true;
        })) {
      return { button: parseButtonFormToPreview(buttonFormData) };
    }
    return {};
  };

  const { list: listPreview, button: buttonPreview } = getPreviewData();

  const handleImport = () => {
    setImportError(null);
    try {
      const parsed = parseMessageToForm(importText);
      if (parsed.type === 'list') {
        setActiveTab('list');
        setListFormData(parsed.list);
      } else {
        setActiveTab('button');
        setButtonFormData(parsed.button);
      }
      setImportOpen(false);
      setImportText('');
    } catch (e: any) {
      setImportError(e?.message || 'Failed to parse message');
    }
  };

  return (
    <div className="h-screen flex flex-col bg-gray-50 overflow-hidden">
      {/* Fixed Header */}
      <div className="flex-shrink-0 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Smartphone className="w-8 h-8 text-green-600" />
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{t('navbar.title')}</h1>
                <p className="text-gray-600">{t('navbar.subtitle')}</p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              {/* Tabs in Navigation */}
              <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as 'list' | 'button')}>
                <TabsList className="grid w-[420px] grid-cols-2">
                  
                  <TabsTrigger value="button" className="flex items-center space-x-2">
                    <MessageSquare className="w-4 h-4" />
                    <span>{t('navbar.buttonMessage')}</span>
                  </TabsTrigger>
                  <TabsTrigger value="list" className="flex items-center space-x-2">
                    <List className="w-4 h-4" />
                    <span>{t('navbar.listMessage')}</span>
                  </TabsTrigger>
                </TabsList>
              </Tabs>
              
              {/* Language Selector */}
              <LanguageSelector />

              {/* Import Message Button */}
              <Button variant="outline" onClick={() => { setImportOpen(true); setImportError(null); }}>
                {t('import.openButton')}
              </Button>
            </div>
          </div>
        </div>
        <div className="bg-amber-50 border-t border-amber-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2 flex items-center justify-center gap-2 text-sm text-amber-800">
            <AlertTriangle className="w-4 h-4 flex-shrink-0" />
            <span>{t('navbar.groupChatWarning')}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 h-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
            
            {/* Left Side - Form Section */}
            <div className="h-full overflow-hidden">
              <div className="h-full overflow-y-auto custom-scrollbar pr-2">
                {activeTab === 'list' ? (
                  <ImprovedListMessageForm
                    onFormChange={handleListFormChange}
                    onGenerate={handleListGenerate}
                    initialValues={listFormData.sections.length ? listFormData : undefined}
                  />
                ) : (
                  <ImprovedButtonMessageForm
                    onFormChange={handleButtonFormChange}
                    onGenerate={handleButtonGenerate}
                    initialValues={buttonFormData.buttons.length ? buttonFormData : undefined}
                  />
                )}
              </div>
            </div>

            {/* Right Side - Preview and Output Section */}
            <div className="h-full overflow-hidden">
              <div className="h-full overflow-y-auto custom-scrollbar pr-2">
                <div className="space-y-4 pb-4">
                  {/* Status indicator */}
                  <div className="sticky top-0 bg-gray-50 pb-4 z-10">
                    <div className="text-center text-sm text-muted-foreground">
                      {generatedMessage ? (
                        <span className="text-green-600 font-medium">{t('preview.statusGenerated')}</span>
                      ) : (
                        <span>{t('preview.statusFillRequired')}</span>
                      )}
                    </div>
                  </div>
              {/* Generated Message - Shows first when available */}
              {generatedMessage && (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      <span>{t('preview.generatedMessage')}</span>
                      <Button
                        onClick={handleCopyMessage}
                        size="sm"
                        variant="outline"
                        className="flex items-center space-x-2"
                      >
                        {copied ? (
                          <>
                            <Check className="w-4 h-4" />
                            <span>{t('common.copied')}</span>
                          </>
                        ) : (
                          <>
                            <Copy className="w-4 h-4" />
                            <span>{t('common.copy')}</span>
                          </>
                        )}
                      </Button>
                    </CardTitle>
                    <CardDescription>
                      {t('preview.description')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="bg-gray-100 rounded-lg p-4">
                      <Textarea
                        value={generatedMessage}
                        readOnly
                        className="font-mono text-sm resize-none bg-transparent border-none p-0"
                        rows={Math.min(10, generatedMessage.split('\n').length)}
                      />
                    </div>
                    <div className="mt-3 flex items-center space-x-2">
                      <Badge variant="secondary">
                        {activeTab === 'list' ? 'List' : 'Button'} Message
                      </Badge>
                      <Badge variant="outline">
                        {generatedMessage.length} {t('common.characters')}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              )}
              
              {/* Preview */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center space-x-2">
                    <Smartphone className="w-5 h-5" />
                    <span>{t('preview.title')}</span>
                  </CardTitle>
                  <CardDescription>
                    {t('preview.subtitle')}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  {activeTab === 'list' && listPreview ? (
                    <ListMessagePreview message={listPreview} />
                  ) : activeTab === 'button' && buttonPreview ? (
                    <ButtonMessagePreview message={buttonPreview} />
                  ) : activeTab === 'list' ? (
                    <SampleListPreview />
                  ) : (
                    <SampleButtonPreview />
                  )}
                </CardContent>
              </Card>

              {/* Import Modal */}
              {importOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
                  <div className="bg-white w-full max-w-xl rounded-lg shadow-lg">
                    <div className="p-4 border-b">
                      <h3 className="text-lg font-semibold">{t('import.title')}</h3>
                      <p className="text-sm text-muted-foreground">{t('import.subtitle')}</p>
                    </div>
                    <div className="p-4 space-y-3">
                      <Textarea
                        value={importText}
                        onChange={(e) => setImportText(e.target.value)}
                        placeholder={t('import.placeholder')}
                        rows={6}
                      />
                      {importError && (
                        <div className="text-sm text-red-600">{t('import.errorPrefix')} {importError}</div>
                      )}
                    </div>
                    <div className="p-4 border-t flex items-center justify-end gap-2">
                      <Button variant="ghost" onClick={() => { setImportOpen(false); setImportText(''); setImportError(null); }}>{t('import.cancel')}</Button>
                      <Button onClick={handleImport}>{t('import.confirm')}</Button>
                    </div>
                  </div>
                </div>
              )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AppWrapper() {
  return (
    <I18nProvider>
      <App />
    </I18nProvider>
  );
}